"use client";

import { useContext } from "react";
import { ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/shadcn/ui/card";
import { DictionaryContext } from "@/context/dictionary-context";
import { Dictionary } from "@/types/hygiena-types";
import { CategoryIcon } from "./category-icon";

interface ContactCardProps {
	title: string;
	icon: string;
	categories: { id: number; title: string; description: string; key?: string }[];
	onSubCategoryClick: (subCategoryTitle: string) => void;
	color: string;
	currentlySelectedSubCategory: string | null;
}

export function ContactCard({
	title,
	icon,
	categories,
	onSubCategoryClick,
	color,
	currentlySelectedSubCategory,
}: ContactCardProps) {
	const t = useContext<Dictionary>(DictionaryContext);
	const translateTitle = (c: ContactCardProps["categories"][number]): string =>
		c.key
			? t?.contact_portal?.[`topic_title_${c.key.replace(/-/g, "_")}`] ?? c.title
			: c.title;
	const translateDesc = (c: ContactCardProps["categories"][number]): string =>
		c.key
			? t?.contact_portal?.[`topic_desc_${c.key.replace(/-/g, "_")}`] ?? c.description
			: c.description;
	return (
		<Card className="overflow-hidden h-full hover-lift">
			<CardHeader style={{ backgroundColor: color }} className="text-white">
				<CardTitle className="flex items-center gap-3">
					<CategoryIcon icon={icon} />
					{title}
				</CardTitle>
			</CardHeader>
			<CardContent className="p-0 flex-1 flex flex-col">
				{categories.length > 0 ? (
					<div className="divide-y flex-1 flex flex-col">
						{categories.map((category, index) => {
							const isSelected =
								category.title === currentlySelectedSubCategory;
							const isLast = index === categories.length - 1;
							return (
								<button
									key={category.id}
									className={`
										w-full text-left p-4 transition-colors flex items-start gap-3 group cursor-pointer
										${isLast ? "mt-auto" : ""}
										${
											isSelected
												? "bg-primary text-white"
												: "bg-white text-slate-900 hover:bg-primary hover:text-white"
										}
									`}
									onClick={() => {
										onSubCategoryClick(category.title);
									}}
									style={{ animationDelay: `${0.05 * index}s` }}
								>
									<div className="flex-1">
										<h3
											className={`font-medium text-lg ${
												isSelected
													? "text-white"
													: "text-black group-hover:text-white"
											}`}
										>
											{translateTitle(category)}
										</h3>
										<p
											className={`text-sm ${
												isSelected
													? "text-white"
													: "text-black/75 group-hover:text-white"
											}`}
										>
											{translateDesc(category)}
										</p>
									</div>
									<ChevronRight
										className={`h-5 w-5 mt-1 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-1 ${
											isSelected
												? "text-white"
												: "text-black/30 group-hover:text-white"
										}`}
									/>
								</button>
							);
						})}
					</div>
				) : (
					<div className="p-6 text-center">
						<p className="text-black/75">{t?.contact_portal?.no_topic_available ?? "No topic available"}</p>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
